import React, { useRef, useState } from 'react';
import './segmented-control.scss';

export interface SegmentOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Shown under the control (with `showHint`) while this option is selected or hovered. */
  hint?: string;
  disabled?: boolean;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentOption<T>[];
  value: T | null;
  onChange: (value: T | null) => void;
  /** Clicking the selected segment clears the choice. For optional facts (BMW project, risk rating). */
  allowEmpty?: boolean;
  /** `sm` fits a table row; `md` is the field size. */
  size?: 'sm' | 'md';
  /** Stretch the segments across the container (the job type picker). */
  fullWidth?: boolean;
  /** Render the hint line. Reserves the line even when empty so the layout does not jump. */
  showHint?: boolean;
  disabled?: boolean;
  'aria-label': string;
  className?: string;
}

/**
 * The editor sidebar's segmented tabs, generalised into a picker: one of a
 * few short options, all visible at once, so there is nothing to open. Uses
 * radio semantics with a roving tabindex and arrow keys.
 *
 * Not typed as React.FC because the component is generic over the option
 * value type; the rest of the primitives follow the usual convention.
 */
function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  allowEmpty = false,
  size = 'md',
  fullWidth = false,
  showHint = false,
  disabled = false,
  'aria-label': ariaLabel,
  className = '',
}: SegmentedControlProps<T>): React.ReactElement {
  const [hovered, setHovered] = useState<T | null>(null);
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);

  const enabledIndexes = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
  const selectedIndex = options.findIndex((o) => o.value === value);
  // The one segment reachable by Tab: the selection, or the first enabled one.
  const tabStop = selectedIndex >= 0 ? selectedIndex : enabledIndexes[0] ?? 0;

  const move = (from: number, dir: 1 | -1) => {
    if (enabledIndexes.length === 0) return;
    const pos = enabledIndexes.indexOf(from);
    const next = enabledIndexes[(pos + dir + enabledIndexes.length) % enabledIndexes.length];
    onChange(options[next].value);
    buttons.current[next]?.focus();
  };

  const handleKey = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      move(index, 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      move(index, -1);
    }
  };

  const hintOption = options.find((o) => o.value === (hovered ?? value));

  return (
    <div
      className={`segmented segmented--${size} ${fullWidth ? 'segmented--full' : ''} ${disabled ? 'is-disabled' : ''} ${className}`.trim()}
    >
      <div className="segmented__track" role="radiogroup" aria-label={ariaLabel}>
        {options.map((option, index) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              ref={(el) => {
                buttons.current[index] = el;
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={index === tabStop ? 0 : -1}
              disabled={disabled || option.disabled}
              className={`segmented__btn ${selected ? 'is-active' : ''}`}
              onClick={() => {
                if (selected) {
                  if (allowEmpty) onChange(null);
                } else {
                  onChange(option.value);
                }
              }}
              onKeyDown={(e) => handleKey(e, index)}
              onMouseEnter={() => setHovered(option.value)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(option.value)}
              onBlur={() => setHovered(null)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {showHint && (
        <p className="segmented__hint" aria-live="polite">
          {hintOption?.hint ?? ' '}
        </p>
      )}
    </div>
  );
}

export default SegmentedControl;
